import { useMemo } from 'react';
import { useAuthSession } from '@app/context/auth-session';

type ActiveOrgMembership = {
  orgId?: string | null;
  orgName?: string | null;
  orgSlug?: string | null;
};

/**
 * Derives the active organization identifier from the authenticated session.
 * Falls back to the first membership if the backend has not yet promoted a
 * dedicated `activeOrg`, mirroring the shell's selector logic.
 */
export const useActiveOrgId = (): string | null => {
  const { user } = useAuthSession();
  return useMemo(() => {
    const memberships = user?.memberships ?? [];
    if (user?.activeOrg?.orgId) {
      return user.activeOrg.orgId;
    }
    return memberships[0]?.orgId ?? null;
  }, [user]);
};

export const useActiveOrgDisplayName = (): string => {
  const { user } = useAuthSession();
  return useMemo(() => {
    const memberships = (user?.memberships ?? []) as ActiveOrgMembership[];
    const activeOrgId = user?.activeOrg?.orgId ?? memberships[0]?.orgId ?? null;
    const membership = memberships.find((item) => item.orgId === activeOrgId) ?? memberships[0];
    const name = membership?.orgName?.trim();
    if (name) {
      return name;
    }
    const slug = membership?.orgSlug?.trim();
    if (slug) {
      return slug;
    }
    if (activeOrgId) {
      return `Org ${activeOrgId.slice(0, 8)}`;
    }
    return 'Organization';
  }, [user]);
};
